import React, { useEffect, useState } from 'react'
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Chip, Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import HistoryIcon from '@mui/icons-material/History'
import { supabase } from '../../supabaseClient'

// Read-only disclosure of prior track_switch_archive rows for a project —
// what switch-project-track archived (not deleted) the last time this
// student's track changed. Renders generically off whatever keys are
// present, so it doesn't need updating if the archived shape ever changes.

const TRACK_LABEL: Record<string, string> = {
  scientific_method:  'Scientific Method',
  engineering_design: 'Engineering Design',
}

// Keys that are large/noisy bookkeeping rather than substantive content —
// skipped so the disclosure stays readable.
const SKIP_KEYS = new Set([
  'ai_feedback', 'mentor_section_comments', 'ai_review', 'ai_review_at', 'last_sent_review',
])

function labelFor(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

function isEmpty(v: unknown): boolean {
  if (v === null || v === undefined) return true
  if (typeof v === 'string') return v.trim() === ''
  if (Array.isArray(v)) return v.length === 0 || v.every(isEmpty)
  if (typeof v === 'object') return Object.keys(v).length === 0
  return false
}

// Recursively render an archived JSON blob as labeled text, skipping empty/
// noisy fields. depth caps how far we descend before falling back to a
// plain label (defensive — archived shapes are trusted but not fixed).
function ReadableValue({ value, depth = 0 }: { value: unknown; depth?: number }) {
  if (isEmpty(value)) return null
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{String(value)}</Typography>
  }
  if (Array.isArray(value)) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {value.filter(v => !isEmpty(v)).map((v, i) => (
          <Box key={i} sx={{ pl: 1.5, borderLeft: '2px solid', borderColor: 'divider' }}>
            <ReadableValue value={v} depth={depth + 1} />
          </Box>
        ))}
      </Box>
    )
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([k, v]) => !SKIP_KEYS.has(k) && !isEmpty(v))
    if (entries.length === 0) return null
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {entries.map(([k, v]) => (
          <Box key={k}>
            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>{labelFor(k)}</Typography>
            <ReadableValue value={v} depth={depth + 1} />
          </Box>
        ))}
      </Box>
    )
  }
  return null
}

interface ArchiveRow {
  id:          string
  from_track:  string
  to_track:    string
  archived:    Record<string, unknown>
  switched_at: string
}

function fmt(iso: string) {
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
}

export default function TrackSwitchArchivePanel({ projectId }: { projectId: string }) {
  const [rows,    setRows]    = useState<ArchiveRow[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const { data } = await supabase
        .from('track_switch_archive')
        .select('id, from_track, to_track, archived, switched_at')
        .eq('project_id', projectId)
        .order('switched_at', { ascending: false })
      if (!cancelled) { setRows((data ?? []) as ArchiveRow[]); setLoading(false) }
    })()
    return () => { cancelled = true }
  }, [projectId])

  if (loading) return null
  if (rows.length === 0) return null

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <HistoryIcon fontSize="small" color="action" />
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Track switch history</Typography>
      </Box>
      {rows.map(r => (
        <Accordion key={r.id} variant="outlined" sx={{ mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2">
              {TRACK_LABEL[r.from_track] ?? r.from_track} <Chip size="small" label="→" sx={{ mx: 0.5, height: 18 }} /> {TRACK_LABEL[r.to_track] ?? r.to_track}
              {' · '}
              <Typography component="span" variant="caption" color="text.secondary">{fmt(r.switched_at)}</Typography>
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Archived from the {TRACK_LABEL[r.from_track] ?? r.from_track} track — preserved, not restorable in-app.
            </Typography>
            <ReadableValue value={r.archived} />
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  )
}
